import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Box, Typography } from '@mui/material';

import type { ReportEmployer, ReportSubOwner } from '../../../../../../../../types';

import { TranslationKeys } from '../../../../../../../../translations';

import {
  ActionLink,
  ListCollapsibleRow,
  ListRow,
  ListRowNoData,
  ListRowSkeleton,
  Pagination,
} from '../../../../../../../../components';

import type { UserAccessesListOnChangeSelected, UserAccessesListSelectedData } from '../../UserAccessesList.types';
import {
  isEmployerSelected,
  handleSelectTopLevelEmployer,
  handleSelectSubEmployerUnderTopLevelEmployer,
  handleSelectAllEmployers,
  handleSelectAllEmployersOnPage,
} from '../../UserAccessesList.utils';

import { UserAccessesListHeader } from '../UserAccessesListHeader';
import { UserAccessesListContainer } from '../UserAccessesListContainer';
import { MuiCheckboxStyled } from '../../../../../../../../components/Checkbox/Checkbox.styles';
import { EditableFieldFactory } from '../../../../../../../../components/FormikField/Factory/EditableFieldFactory';

type UserAccessesListEmployersProps = {
  isLoading?: boolean;
  isLoggedInUser?: boolean;
  onChangeSelected: UserAccessesListOnChangeSelected;
  selectedData: UserAccessesListSelectedData | undefined;
  employers: Array<ReportEmployer> | undefined;
  selectedEventFlows: Array<string> | undefined;
  openConfirmationDialog: (value: boolean) => void;
  subOwners?: Array<ReportSubOwner>;
  page?: number;
  limit?: number;
  totalItems?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  searchQuery?: string;
  onSearchQueryChange?: (query: string) => void;
};

export const UserAccessesListEmployers = React.memo(function UserAccessesListEmployers({
  employers,
  selectedData,
  onChangeSelected,
  selectedEventFlows,
  openConfirmationDialog,
  isLoggedInUser = false,
  isLoading = false,
  subOwners,
  page = 1,
  limit = 25,
  totalItems = 0,
  totalPages = 0,
  onPageChange,
  onLimitChange,
  searchQuery: externalSearchQuery = '',
  onSearchQueryChange,
}: UserAccessesListEmployersProps) {
  const searchQueryRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedDataRef = React.useRef(selectedData);
  const openConfirmationDialogRef = React.useRef(openConfirmationDialog);

  React.useEffect(() => {
    selectedDataRef.current = selectedData;
  }, [selectedData]);

  React.useEffect(() => {
    openConfirmationDialogRef.current = openConfirmationDialog;
  }, [openConfirmationDialog]);

  const listData = employers;

  const selectedEmployerIds =
    selectedData?.employers === '*'
      ? employers?.flatMap(emp => [emp.employerId, ...(emp.subEmployers?.map(s => s.employerId) || [])]) || []
      : Object.keys((selectedData?.employers as Record<string, string>) || {});
  const disabledIfLoadingOrIsLoggedInUser = isLoggedInUser || isLoading;
  const hasData = listData && listData?.length > 0;
  const showData = !isLoading && hasData;
  const showNoData = !isLoading && !showData;

  const onPaginationChange = React.useCallback(
    ({ page: newPage, rowSize }: { page?: number; rowSize?: number }) => {
      if (newPage !== undefined) {
        onPageChange?.(newPage);
      }
      if (rowSize !== undefined) {
        onLimitChange?.(rowSize);
        onPageChange?.(1);
      }
    },
    [onPageChange, onLimitChange],
  );

  const onChangeSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (searchQueryRef.current) {
      clearTimeout(searchQueryRef.current);
    }
    searchQueryRef.current = setTimeout(() => {
      onSearchQueryChange?.(value);
      onPageChange?.(1);
    }, 300);
  };

  const onSelectAll = React.useCallback(() => {
    const currentSelectedData = selectedDataRef.current;
    if (!currentSelectedData) return;

    const result = handleSelectAllEmployers(currentSelectedData, subOwners);
    onChangeSelected(result);
  }, [onChangeSelected, subOwners]);

  const onSelectAllOnPage = React.useCallback(() => {
    const currentSelectedData = selectedDataRef.current;
    if (!currentSelectedData) return;

    const result = handleSelectAllEmployersOnPage(currentSelectedData, employers, subOwners);
    onChangeSelected(result);
  }, [employers, onChangeSelected, subOwners]);

  const onSelectEmployer = React.useCallback(
    (employer: ReportEmployer) => () => {
      const currentSelectedData = selectedDataRef.current;
      if (!currentSelectedData) return;

      const result = handleSelectTopLevelEmployer(currentSelectedData, employer);
      onChangeSelected(result);
    },
    [onChangeSelected],
  );

  const onSelectSubEmployer = React.useCallback(
    (parentEmployer: ReportEmployer, subEmployerId: string) => () => {
      const currentSelectedData = selectedDataRef.current;
      if (!currentSelectedData) return;

      const result = handleSelectSubEmployerUnderTopLevelEmployer(currentSelectedData, parentEmployer, subEmployerId);
      onChangeSelected(result);
    },
    [onChangeSelected],
  );

  const onSelectEventFlow = React.useCallback(
    (employerId: string, eventFlowDefaultUser: string | undefined) => () => {
      const currentSelectedData = selectedDataRef.current;
      if (!currentSelectedData) return;

      const currentEventFlows = currentSelectedData.eventFlows || [];
      const isSelected = currentEventFlows.includes(employerId);

      const newEventFlows = isSelected
        ? currentEventFlows.filter(id => id !== employerId)
        : [...currentEventFlows, employerId];

      openConfirmationDialogRef.current(eventFlowDefaultUser != null);

      onChangeSelected({
        ...currentSelectedData,
        eventFlows: newEventFlows,
      });
    },
    [onChangeSelected],
  );

  const renderSubEmployers = React.useCallback(
    (employer: ReportEmployer) => {
      return employer.subEmployers?.map(subEmployer => {
        const isSelected = isEmployerSelected(selectedData, subEmployer.employerId);
        const isEventFlowSelected = selectedEventFlows?.includes(subEmployer.employerId);

        return (
          <ListRow
            key={`${employer.employerId}_${subEmployer.employerId}`}
            selected={isSelected}
            onSelect={onSelectSubEmployer(employer, subEmployer.employerId)}
            disabled={disabledIfLoadingOrIsLoggedInUser}
            innerRow
          >
            <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gap={2}>
              <Typography>
                <ActionLink to={`/employers/${subEmployer.employerId}`}>{subEmployer.name}</ActionLink>
              </Typography>
            </Box>
            <Typography sx={{ marginLeft: 'auto', padding: '0 10px 0 0' }}>
              <MuiCheckboxStyled
                color={'secondary'}
                onClick={onSelectEventFlow(subEmployer.employerId, subEmployer.eventFlowDefaultUser)}
                checked={!!isEventFlowSelected && isSelected}
                disabled={!isSelected || disabledIfLoadingOrIsLoggedInUser}
              />
              <Typography variant={'caption'} textTransform={'initial'}>
                <FormattedMessage id={TranslationKeys.users_accessesList_defaultEventFlowUser} />
              </Typography>
            </Typography>
          </ListRow>
        );
      });
    },
    [selectedData, selectedEventFlows, onSelectSubEmployer, onSelectEventFlow, disabledIfLoadingOrIsLoggedInUser],
  );

  return (
    <Box sx={theme => ({ marginTop: theme.spacing(3) })}>
      <UserAccessesListHeader
        headerTextId={TranslationKeys.users_accessesList_employers}
        onChange={onChangeSearchInput}
        isLoading={isLoading}
        disabled={disabledIfLoadingOrIsLoggedInUser}
        onSelectAll={onSelectAll}
        onSelectAllOnPage={onSelectAllOnPage}
        areAllSelected={selectedData?.employers === '*'}
        areAllOnPageSelected={
          !!employers?.length && employers?.every(employer => isEmployerSelected(selectedData, employer.employerId))
        }
        noData={listData?.length === 0}
        totalCount={totalItems}
      />

      <EditableFieldFactory
        editing={true}
        field={{
          type: 'checkbox',
          name: 'autoAssignEmployers',
          label: useIntl().formatMessage({ id: TranslationKeys.users_accessesList_autoAssignEmployer }),
        }}
        value={selectedData?.autoAssignEmployers}
        onChange={(event: any) => {
          if (selectedData) {
            onChangeSelected({
              ...selectedData,
              autoAssignEmployers: event.value,
            });
          }
        }}
      />

      <UserAccessesListContainer>
        {isLoading && <ListRowSkeleton />}

        {showData &&
          listData?.map(employer => {
            const isSelected = isEmployerSelected(selectedData, employer.employerId);
            const isEventFlowSelected = selectedEventFlows?.includes(employer.employerId);
            const hasSubEmployers = employer.subEmployers && employer.subEmployers.length > 0;

            const selectedSubEmployerCount = hasSubEmployers
              ? employer.subEmployers!.filter(subEmp => selectedEmployerIds.includes(subEmp.employerId)).length
              : 0;

            const rowContent = (
              <>
                <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gap={2}>
                  <Typography>
                    <ActionLink to={`/employers/${employer.employerId}`}>{employer.name}</ActionLink>
                  </Typography>
                  {hasSubEmployers && (
                    <Typography variant={'caption'} textTransform={'initial'}>
                      <FormattedMessage
                        id={TranslationKeys.users_accessesList_subEmployers}
                        values={{ count: employer.subEmployers?.length }}
                      />
                    </Typography>
                  )}
                  {selectedSubEmployerCount > 0 && (
                    <Typography variant={'caption'} textTransform={'initial'}>
                      <FormattedMessage
                        id={TranslationKeys.users_accessesList_selectedCountEmployers}
                        values={{ count: selectedSubEmployerCount }}
                      />
                    </Typography>
                  )}
                </Box>
                <Typography sx={{ marginLeft: 'auto', padding: '0 10px 0 0' }}>
                  <MuiCheckboxStyled
                    color={'secondary'}
                    onClick={onSelectEventFlow(employer.employerId, employer.eventFlowDefaultUser)}
                    checked={!!isEventFlowSelected && isSelected}
                    disabled={!isSelected || disabledIfLoadingOrIsLoggedInUser}
                  />
                  <Typography variant={'caption'} textTransform={'initial'}>
                    <FormattedMessage id={TranslationKeys.users_accessesList_defaultEventFlowUser} />
                  </Typography>
                </Typography>
              </>
            );

            if (hasSubEmployers) {
              return (
                <ListCollapsibleRow
                  key={employer.employerId}
                  selected={isSelected}
                  onSelect={onSelectEmployer(employer)}
                  disabled={disabledIfLoadingOrIsLoggedInUser}
                  rowHeader={rowContent}
                >
                  {renderSubEmployers(employer)}
                </ListCollapsibleRow>
              );
            }

            return (
              <ListRow
                key={employer.employerId}
                selected={isSelected}
                onSelect={onSelectEmployer(employer)}
                disabled={disabledIfLoadingOrIsLoggedInUser}
              >
                {rowContent}
              </ListRow>
            );
          })}
        {showNoData && <ListRowNoData searchQuery={externalSearchQuery} />}
      </UserAccessesListContainer>

      {/* Pagination - only show for non-super-admin users with server-side pagination */}
      {totalPages > 0 && (
        <Pagination
          count={totalItems}
          page={page}
          rowsPerPage={limit}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPaginationChange={onPaginationChange}
        />
      )}
    </Box>
  );
});
